import React, { useState } from 'react';
import { Upload, File, Download, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader, formatFileSize } from './vaultUtils';

const FileUploadSection = ({ 
  mode, 
  entry, 
  files, 
  setFiles, 
  pendingFiles, 
  setPendingFiles, 
  uploading, 
  setUploading, 
  fileInputRef 
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  const processFiles = async (selectedFiles) => {
    if (selectedFiles.length === 0) return;

    // Filter valid file types
    const validTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xls', '.xlsx'];
    const validFiles = selectedFiles.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return validTypes.includes(ext);
    });

    if (validFiles.length !== selectedFiles.length) {
      toast.error('Some files were skipped. Only PDF, Images, and Documents are allowed.');
    }

    if (validFiles.length === 0) return;

    // Check file size (max 5MB per file)
    const oversizedFiles = validFiles.filter(file => file.size > MAX_FILE_SIZE);
    const sizedFiles = validFiles.filter(file => file.size <= MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      oversizedFiles.forEach(file => {
        toast.error(`"${file.name}" exceeds 5MB limit (${formatFileSize(file.size)}). Please upload a smaller file.`);
      });
    }

    if (sizedFiles.length === 0) return;

    // For new entries (add mode), store files locally as pending
    if (mode === 'add') {
      setPendingFiles(prev => [...prev, ...sizedFiles]);
      toast.success(`${sizedFiles.length} file(s) selected. Will be uploaded when you save.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // For existing entries (edit mode), upload immediately
    setUploading(true);
    
    for (const file of sizedFiles) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/client/vault/entry/${entry.id}/upload`,
          formDataUpload,
          {
            headers: {
              ...getAuthHeader(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        setFiles(prev => [...prev, {
          id: response.data.file_id,
          original_name: response.data.filename,
          size: response.data.size,
          uploaded_at: new Date().toISOString()
        }]);
        
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    await processFiles(selectedFiles);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    await processFiles(droppedFiles);
  };

  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;

    try {
      await axios.delete(
        `${API}/client/vault/entry/${entry.id}/file/${fileId}`,
        { headers: getAuthHeader() }
      );
      
      setFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('File deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    }
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(
        `${API}/client/vault/entry/${entry.id}/file/${fileId}/download`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-white font-medium border-b border-slate-700 pb-2 flex items-center gap-2">
        <Upload size={18} className="text-blue-400" />
        Attachments
      </h3>
      
      {/* Upload Area - Supports Click and Drag & Drop */}
      <div 
        className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl transition-all cursor-pointer ${
          isDragging 
            ? 'border-blue-500 bg-blue-500/10 scale-[1.02]' 
            : 'border-slate-700 hover:border-blue-500/50'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        data-testid="file-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            <span className="text-slate-400 text-sm">Uploading...</span>
          </div>
        ) : isDragging ? (
          <div className="flex flex-col items-center gap-2">
            <div className="p-3 rounded-full bg-blue-500/20">
              <Upload className="w-6 h-6 text-blue-400 animate-bounce" />
            </div>
            <span className="text-blue-400 text-sm font-medium">Drop files here</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="p-3 rounded-full bg-blue-500/10">
              <Upload className="w-6 h-6 text-blue-400" />
            </div>
            <span className="text-slate-300 text-sm font-medium">Drag & drop files or click to upload</span>
            <span className="text-slate-500 text-xs">PDF, Images, Documents (Max 5MB each)</span>
          </div>
        )}
      </div>

      {/* Pending Files (for new entries) */}
      {pendingFiles && pendingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-amber-400 text-xs font-medium">{pendingFiles.length} file(s) ready to upload on save</p>
          {pendingFiles.map((file, index) => (
            <div 
              key={index} 
              className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/30"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <File className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium truncate max-w-[200px]">
                    {file.name}
                  </p>
                  <p className="text-amber-400/70 text-xs">
                    {formatFileSize(file.size)} • Pending upload
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => handleRemovePendingFile(index)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <X size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Existing Files List (for edit mode) */}
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-slate-400 text-xs font-medium">{files.length} file(s) attached</p>
          {files.map((file) => (
            <div 
              key={file.id} 
              className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <File className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium truncate max-w-[200px]">
                    {file.original_name}
                  </p>
                  <p className="text-slate-500 text-xs">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDownloadFile(file.id, file.original_name)}
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                >
                  <Download size={14} />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteFile(file.id, file.original_name)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;
